import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { getPendingSiteVisits } from '../../services/api/implementation/impl';
import SiteVisitOutcomeModal from './SiteVisitOutcomeModal';
import { PENDING_SITE_VISITS_QUERY_KEY, formatVisitTime } from './siteVisitOutcome';

const SHOWN = 5;

/**
 * Site visits whose time has passed with no outcome recorded, so none get
 * missed. Each row opens the outcome modal in place.
 */
export default function PendingSiteVisitsCard({ projectId }) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: [...PENDING_SITE_VISITS_QUERY_KEY, projectId || null],
    queryFn: () => getPendingSiteVisits({ projectId, limit: SHOWN }),
    staleTime: 60 * 1000
  });

  const items = data?.data || [];
  const total = data?.meta?.total ?? items.length;

  return (
    <div className="rounded-2xl border border-slate-200/90 bg-white px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <MapPin className="h-4 w-4 text-indigo-500" />
          Site visits awaiting outcome
        </p>
        {total > 0 ? (
          <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800">
            {total}
          </span>
        ) : null}
      </div>

      {isLoading ? (
        <p className="mt-2 text-xs text-slate-500">Loading…</p>
      ) : isError ? (
        <p className="mt-2 text-xs text-red-700">Could not load site visits.</p>
      ) : !items.length ? (
        <p className="mt-2 text-xs text-slate-500">
          Nothing waiting — every past visit has an outcome.
        </p>
      ) : (
        <ul className="mt-2 divide-y divide-slate-100">
          {items.map((item) => (
            <li
              key={item.follow_up_id}
              className="flex flex-wrap items-center justify-between gap-2 py-2">
              <button
                type="button"
                onClick={() => navigate(`/builder/sales/${item.lead_id}`)}
                className="min-w-0 text-left">
                <span className="block truncate text-sm font-medium text-slate-900 hover:underline">
                  {item.lead_name || `Lead #${item.lead_id}`}
                </span>
                <span className="block text-xs text-slate-500">
                  {formatVisitTime(item.meeting?.start_time || item.visit_at) || '—'}
                  {item.assignee?.name ? ` · ${item.assignee.name}` : ''}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setSelected(item)}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-800 hover:bg-slate-50">
                Record outcome
              </button>
            </li>
          ))}
        </ul>
      )}

      {total > items.length ? (
        <p className="mt-1 text-xs text-slate-500">+{total - items.length} more</p>
      ) : null}

      {selected ? (
        <SiteVisitOutcomeModal
          leadId={selected.lead_id}
          leadName={selected.lead_name}
          visitAt={selected.meeting?.start_time || selected.visit_at}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </div>
  );
}
